<script setup lang="ts">
import { DLVFavoriteKey } from '~/lib/models/dlv-modal'
import { entriesFromObject } from '~/types/typeUtils'

const props = defineProps<{
  checked: boolean
  alias: string
}>()

type KeyType = 'home' | 'work' | 'other' | undefined

const { t } = useLocale()

defineEmits(['update:checked', 'update:alias'])

const checkedState = useVModel(props, 'checked')
const aliasState = useVModel(props, 'alias')
const touched = ref(false)
const keyType = ref<KeyType>()

const keyTypes = computed(() => {
  return {
    home: {
      translation: t('selectRestaurant.DLV.favorite.keys.home'),
      image: '/images/icons/favorite-addresses/home.svg',
    },
    work: {
      translation: t('selectRestaurant.DLV.favorite.keys.work'),
      image: '/images/icons/favorite-addresses/work.svg',
    },
    other: {
      translation: t('selectRestaurant.DLV.favorite.keys.other'),
      image: '/images/icons/favorite-addresses/other.svg',
    },
  }
})

const toggle = () => {
  checkedState.value = !checkedState.value
  if (!checkedState.value) {
    touched.value = false
    aliasState.value = ''
    keyType.value = undefined
  }
}

const setKeyType = (key: KeyType) => {
  if (key === keyType.value) return
  const hasPreviousKeyType = keyType.value !== undefined
  keyType.value = key
  if (key === 'home') aliasState.value = DLVFavoriteKey.home
  else if (key === 'work') aliasState.value = DLVFavoriteKey.work
  else if (hasPreviousKeyType) {
    aliasState.value = ''
    touched.value = true
  }
}

watch(aliasState, () => {
  if (!touched.value && aliasState.value) touched.value = true
})

onMounted(() => {
  if (!aliasState.value) return
  if (aliasState.value === DLVFavoriteKey.home) keyType.value = 'home'
  else if (aliasState.value === DLVFavoriteKey.work) keyType.value = 'work'
  else keyType.value = 'other'
})
</script>

<template>
  <section class="my-4">
    <div class="flex flex-row items-center gap-3">
      <img
        data-test="dlv-fav-toggle"
        class="cursor-pointer"
        :src="
          checkedState
            ? '/images/icons/favoriteFilled.svg'
            : '/images/icons/profileSections/favorite.svg'
        "
        @click="toggle"
      />
      <p class="text-sm">{{ t('selectRestaurant.DLV.favorite.saveTitle') }}</p>
    </div>

    <div
      v-if="checkedState"
      class="mt-6 flex w-full flex-col items-start gap-4 md:flex-row md:items-center"
    >
      <!-- Fixed value pills (Home, Work, Other ) -->
      <div class="w-full md:max-w-[50%]">
        <p class="mb-4 text-sm font-bold">{{ t('selectRestaurant.DLV.favorite.formTitle') }}</p>

        <div class="flex flex-row flex-wrap gap-2">
          <div
            v-for="[key, pill] in entriesFromObject(keyTypes)"
            :key
            class="flex w-fit cursor-pointer flex-row items-center gap-2 rounded-[100px] border border-mcd-black px-4 py-[6px]"
            :class="{ 'bg-mcd-main': key === keyType }"
            :data-test="`fav-pill-${key}`"
            @click="setKeyType(key)"
          >
            <span class="text-xs font-bold">{{ pill.translation }}</span>
            <img class="h-auto w-4" :src="pill.image" />
          </div>
        </div>
      </div>

      <!-- Input (only for other) -->
      <div v-if="keyType === 'other'" class="mb-8 w-full flex-1 md:mb-0">
        <McInput
          v-model="aliasState"
          :label="t('selectRestaurant.DLV.favorite.fields.alias.label')"
          :placeholder="t('selectRestaurant.DLV.favorite.fields.alias.label')"
          name="alias"
          type="text"
        />

        <p v-if="touched && !aliasState" class="VError mt-1 text-xs text-mcd-red">
          {{ t('selectRestaurant.DLV.favorite.fields.alias.required') }}
        </p>
      </div>
    </div>
  </section>
</template>
